/**
 * Dialog for configuring chart settings.
 * Uses TanStack Form for form state management.
 * Supports multiple Y-axis columns for comparing values on the same chart.
 */

import { useMemo, useEffect, useCallback } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import type { QueryColumn } from '../../../../types/queryExecution';
import type {
  ChartConfig,
  ChartType,
  AggregationFunction,
  ColumnTypeInfo,
  BarChartVariant,
  AreaChartVariant,
} from '../types/chartTypes';
import {
  CHART_TYPES,
  CHART_TYPE_LABELS,
  BAR_CHART_VARIANTS,
  BAR_CHART_VARIANT_LABELS,
  AREA_CHART_VARIANTS,
  AREA_CHART_VARIANT_LABELS,
} from '../types/chartTypes';
import { analyzeColumns, getYAxisCandidates } from '../utils/columnTypeDetection';
import { suggestChartType, hasChartableData } from '../utils/chartConfigValidation';
import { labelOverridesForKeys } from '../utils/chartLabels';
import { resolveChartConfigColumnRefs } from '../utils/chartColumnRefs';
import { YAxisEntryRow } from './YAxisEntryRow';

type ChartBuilderDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly columns: QueryColumn[];
  readonly rows: Record<string, unknown>[];
  readonly existingConfig?: ChartConfig;
  readonly onConfigured: (config: ChartConfig) => void;
  /** Precomputed spatial column ids (Task 27, A19/G18) — pass the caller's already-
   *  computed native + declared spatial column set (e.g. `useSpatialResultColumns`)
   *  so WKT/GeoJSON-declared columns the value-sniff fallback can't see are still
   *  excluded from every axis/series picker. Optional — `analyzeColumns` falls back
   *  to its own dataType/value-sniff detection when omitted. */
  readonly spatialColumnIds?: ReadonlySet<string>;
};

type YEntry = {
  column: string;
  aggregation: AggregationFunction;
};

type FormValues = {
  type: ChartType;
  xColumn: string;
  yEntries: YEntry[];
  seriesColumn: string;
  title: string;
  showLegend: boolean;
  showGrid: boolean;
  barVariant: BarChartVariant;
  areaVariant: AreaChartVariant;
};

const MAX_Y_ENTRIES = 4;
const SINGLE_Y_TYPES: ChartType[] = ['pie', 'scatter'];

export function ChartBuilderDialog({
  open,
  onOpenChange,
  columns,
  rows,
  existingConfig,
  onConfigured,
  spatialColumnIds,
}: ChartBuilderDialogProps) {
  const columnInfos = useMemo<ColumnTypeInfo[]>(() => {
    if (!columns || !rows) return [];
    return analyzeColumns(columns, rows, spatialColumnIds);
  }, [columns, rows, spatialColumnIds]);

  const numericColumns = useMemo(
    () => getYAxisCandidates(columnInfos),
    [columnInfos]
  );

  const { chartable, reason } = useMemo(
    () => hasChartableData(rows.length, columnInfos),
    [rows.length, columnInfos]
  );

  const suggestedType = useMemo(
    () => suggestChartType(columnInfos),
    [columnInfos]
  );

  const defaultValues = useMemo<FormValues>(() => {
    if (existingConfig) {
      // Resolve a persisted config's column references (row KEY, unique NAME,
      // or an older ipynb/dashboard config's blank/whitespace NAME rekeyed to
      // `col_<i>`) against the CURRENT result set's columns before seeding
      // the pickers — otherwise a stale NAME-based reference preselects
      // nothing (P2 review finding companion: the shaping fix alone doesn't
      // help the builder's own dropdowns).
      const resolvedConfig = resolveChartConfigColumnRefs(existingConfig, columnInfos);
      return {
        type: resolvedConfig.type,
        xColumn: resolvedConfig.xAxis.column,
        yEntries: resolvedConfig.yAxis.map((y) => ({
          column: y.column,
          aggregation: y.aggregation || 'sum',
        })),
        seriesColumn: resolvedConfig.series?.column || '',
        title: resolvedConfig.title || '',
        showLegend: resolvedConfig.showLegend,
        showGrid: resolvedConfig.showGrid,
        barVariant: resolvedConfig.barVariant || 'grouped',
        areaVariant: resolvedConfig.areaVariant || 'overlapping',
      };
    }

    const firstString = columnInfos.find((c) => c.category === 'string');
    const firstDate = columnInfos.find((c) => c.category === 'date');
    const firstNumeric = numericColumns[0];

    return {
      type: suggestedType,
      xColumn: firstDate?.key || firstString?.key || columnInfos[0]?.key || '',
      yEntries: [{ column: firstNumeric?.key || '', aggregation: 'sum' as AggregationFunction }],
      seriesColumn: '',
      title: '',
      showLegend: true,
      showGrid: true,
      barVariant: 'grouped' as BarChartVariant,
      areaVariant: 'overlapping' as AreaChartVariant,
    };
  }, [existingConfig, columnInfos, numericColumns, suggestedType]);

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      // Config columns hold the ROW key; when the key differs from the header
      // label (`col_<i>` for a duplicate or blank name) the label rides along
      // so legend/axis/tooltip copy stays what the grid shows. Disambiguated
      // together (X first, then each Y) so two same-named columns never
      // collapse into one chart data key downstream (chartLabels.ts).
      const [xLabel, ...yLabels] = labelOverridesForKeys(
        [value.xColumn, ...value.yEntries.map((e) => e.column)],
        columnInfos
      );
      const config: ChartConfig = {
        type: value.type,
        xAxis: { column: value.xColumn, ...xLabel },
        yAxis: value.yEntries.map((e, i) => ({
          column: e.column,
          aggregation: e.aggregation,
          ...yLabels[i],
        })),
        series: value.seriesColumn ? { column: value.seriesColumn } : undefined,
        title: value.title || undefined,
        showLegend: value.showLegend,
        showGrid: value.showGrid,
        barVariant: value.type === 'bar' ? value.barVariant : undefined,
        areaVariant: value.type === 'area' ? value.areaVariant : undefined,
      };
      onConfigured(config);
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [open, form]);

  const canSubmit = useStore(form.store, (state) => state.canSubmit);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const currentType = useStore(form.store, (state) => state.values.type);
  const currentSeriesColumn = useStore(form.store, (state) => state.values.seriesColumn);
  const currentYEntries = useStore(form.store, (state) => state.values.yEntries);

  const isSingleYType = SINGLE_Y_TYPES.includes(currentType);
  const hasMultipleY = currentYEntries.length > 1;
  const showBarVariant = currentType === 'bar' && (currentSeriesColumn || hasMultipleY);
  const showAreaVariant = currentType === 'area' && (currentSeriesColumn || hasMultipleY);
  const canAddY = !isSingleYType && currentYEntries.length < MAX_Y_ENTRIES;

  // Task 27 — the raw column list (unlike numericColumns/getYAxisCandidates
  // above and the Group By filter below) is built independently of the
  // category-filtered candidate lists, so spatial columns must be excluded
  // here explicitly via `columnInfos` (the single `analyzeColumns` seam)
  // rather than left to leak into the X-axis / Y-axis-fallback pickers.
  const columnOptions = columnInfos
    .filter((col) => col.category !== 'unsupported')
    .map((col) => ({
      value: col.key,
      label: col.name,
      dataType: col.dataType,
    }));

  // When chart type switches to pie/scatter, trim Y entries to 1
  const handleTypeChange = useCallback((newType: ChartType) => {
    form.setFieldValue('type', newType);
    if (SINGLE_Y_TYPES.includes(newType)) {
      const entries = form.getFieldValue('yEntries');
      if (entries.length > 1) {
        form.setFieldValue('yEntries', [entries[0]]);
      }
    }
  }, [form]);

  const handleAddYEntry = useCallback(() => {
    const entries = form.getFieldValue('yEntries');
    if (entries.length >= MAX_Y_ENTRIES) return;
    form.setFieldValue('yEntries', [...entries, { column: '', aggregation: 'sum' as AggregationFunction }]);
  }, [form]);

  const handleRemoveYEntry = useCallback((index: number) => {
    const entries = form.getFieldValue('yEntries');
    if (entries.length <= 1) return;
    form.setFieldValue('yEntries', entries.filter((_, i) => i !== index));
  }, [form]);

  const handleYColumnChange = useCallback((index: number, column: string) => {
    const entries = form.getFieldValue('yEntries');
    const updated = entries.map((e, i) => i === index ? { ...e, column } : e);
    form.setFieldValue('yEntries', updated);
  }, [form]);

  const handleYAggregationChange = useCallback((index: number, aggregation: AggregationFunction) => {
    const entries = form.getFieldValue('yEntries');
    const updated = entries.map((e, i) => i === index ? { ...e, aggregation } : e);
    form.setFieldValue('yEntries', updated);
  }, [form]);

  if (!chartable) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[400px]"
          data-testid="chart-builder-dialog-error"
        >
          <DialogHeader>
            <DialogTitle>Cannot Create Chart</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-muted-foreground">{reason}</div>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="chart-builder-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {existingConfig ? 'Edit Chart' : 'Create Chart'}
          </DialogTitle>
        </DialogHeader>

        <form
          id="chart-builder-form"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
          className="space-y-3"
        >
          {/* Chart Type */}
          <form.Field name="type">
            {(field) => (
              <div className="grid gap-1">
                <Label>Chart Type</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => handleTypeChange(value as ChartType)}
                >
                  <SelectTrigger data-testid="chart-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHART_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {CHART_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          {/* X-Axis */}
          <form.Field name="xColumn">
            {(field) => (
              <div className="grid gap-1">
                <Label>
                  {currentType === 'scatter' ? 'X-Axis' : 'Category (X-Axis)'}
                </Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                >
                  <SelectTrigger data-testid="x-column-select">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOptions.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        <span className="truncate">{col.label}</span>
                        {col.dataType && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({col.dataType})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          {/* Y-Axis Entries */}
          <div className="grid gap-1">
            <Label>Values (Y-Axis)</Label>
            <div className="space-y-2">
              {currentYEntries.map((entry, index) => (
                <YAxisEntryRow
                  key={index}
                  index={index}
                  column={entry.column}
                  aggregation={entry.aggregation}
                  numericColumns={numericColumns}
                  allColumnOptions={columnOptions}
                  showRemove={currentYEntries.length > 1}
                  onColumnChange={(col) => handleYColumnChange(index, col)}
                  onAggregationChange={(agg) => handleYAggregationChange(index, agg)}
                  onRemove={() => handleRemoveYEntry(index)}
                />
              ))}
            </div>
            {canAddY && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-1 w-fit"
                onClick={handleAddYEntry}
                data-testid="add-y-value-button"
              >
                <Plus className="mr-1 h-4 w-4" />
                Add Value
              </Button>
            )}
          </div>

          {/* Series (Group By) */}
          {currentType !== 'pie' && (
            <form.Field name="seriesColumn">
              {(field) => (
                <div className="grid gap-1">
                  <Label>Group By</Label>
                  <Select
                    value={field.state.value || '__none__'}
                    onValueChange={(value) =>
                      field.handleChange(value === '__none__' ? '' : value)
                    }
                  >
                    <SelectTrigger data-testid="series-column-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {columnOptions
                        .filter((col) => {
                          const info = columnInfos.find(
                            (c) => c.key === col.value
                          );
                          return (
                            info?.category === 'string' ||
                            info?.category === 'boolean'
                          );
                        })
                        .map((col) => (
                          <SelectItem key={col.value} value={col.value}>
                            {col.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          {/* Bar Variant */}
          {showBarVariant && (
            <form.Field name="barVariant">
              {(field) => (
                <div className="grid gap-1">
                  <Label>Bar Style</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as BarChartVariant)
                    }
                  >
                    <SelectTrigger data-testid="bar-variant-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BAR_CHART_VARIANTS.map((variant) => (
                        <SelectItem key={variant} value={variant}>
                          {BAR_CHART_VARIANT_LABELS[variant]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          {/* Area Variant */}
          {showAreaVariant && (
            <form.Field name="areaVariant">
              {(field) => (
                <div className="grid gap-1">
                  <Label>Area Style</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as AreaChartVariant)
                    }
                  >
                    <SelectTrigger data-testid="area-variant-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AREA_CHART_VARIANTS.map((variant) => (
                        <SelectItem key={variant} value={variant}>
                          {AREA_CHART_VARIANT_LABELS[variant]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          {/* Title */}
          <form.Field name="title">
            {(field) => (
              <div className="grid gap-1">
                <Label>Title</Label>
                <Input
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Optional"
                  data-testid="chart-title-input"
                />
              </div>
            )}
          </form.Field>

          {/* Options */}
          <div className="flex items-center gap-6 pt-1">
            <form.Field name="showLegend">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                    data-testid="show-legend-switch"
                  />
                  <Label className="cursor-pointer text-sm">Legend</Label>
                </div>
              )}
            </form.Field>

            <form.Field name="showGrid">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                    data-testid="show-grid-switch"
                  />
                  <Label className="cursor-pointer text-sm">Grid</Label>
                </div>
              )}
            </form.Field>
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="chart-builder-form"
            disabled={!canSubmit || isSubmitting}
            data-testid="chart-builder-apply"
          >
            {existingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
